import React, { RefObject, useEffect, useRef, useState } from "react";
import { CALENDAR_INVITES, DEFAULT_ERROR_MESSAGE } from "../../lib/constants";
import { useToast } from "../passport/NotificationWrapper/NotificationProvider";
import Icon from "../../components/_base/Icon";
import { CurrentUserResponseType, RegistrationSubscriptionParams } from "../../lib/api";
import Buttons from '../../components/button';
import { preRegisterToCalendarInvite, subscribeToCalendarInvite } from "../../lib/api/protected";
import './SubscribeCalendarButton.scss';
import { useCalendarInviteSubscription } from "../../hooks/use-calendar-subscription";

enum LoadingState {
  None,
  Subscribing,
  Unsubscribing,
}

interface SubscriptionCalendarButtonsProps {
  eventId: string;
  name: string;
  attendeeSubscribed: boolean;
  calendarInvite: CALENDAR_INVITES;
  data: CurrentUserResponseType | undefined;
  eventTitleForPassport?: string;
  downloadIcsFile: () => Promise<void>;
  openGoogleCalendar: () => Promise<void>;
  unSubscribeToCalendar: (registrationId: string) => Promise<void>;
  subscribeButtonLabel: string;
  subscribedButtonLabel: string;
  className: string;
  isSubscribeToEvent?: boolean;
  updateSubscribe: (subscribed: boolean) => void;
  meetingId?: string;
  isEventLevelSubscription?: boolean;
  withOptionsBoxStyle?: boolean;
  withCalendarIcon?: boolean;
  onShowDropDown?: (ref: RefObject<HTMLDivElement>) => void;
  customStyles?: React.CSSProperties;
  iconButton?: boolean;
}

const SubscriptionCalendarButtons: React.FC<SubscriptionCalendarButtonsProps> = ({
  eventId,
  name,
  attendeeSubscribed,
  calendarInvite,
  data,
  eventTitleForPassport,
  downloadIcsFile,
  openGoogleCalendar,
  unSubscribeToCalendar,
  subscribeButtonLabel,
  subscribedButtonLabel,
  isSubscribeToEvent,
  className,
  updateSubscribe,
  meetingId,
  isEventLevelSubscription,
  withOptionsBoxStyle,
  withCalendarIcon,
  iconButton,
  onShowDropDown,
  customStyles
}) => {
  const { state, subscribe } = useCalendarInviteSubscription();
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.None);
  const { addToast } = useToast();
  const customClassName = `rounded on-hover-shadow flex ${isEventLevelSubscription ? 'mt-4' : ''} ${iconButton ? '' : 'h-10'}`;
  const iconSize = iconButton ? '25px' : '20px';
  const isButtonDisabled = loadingState !== LoadingState.None;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    if (state.subscribed !== null) {
      handleSubscriptionResult(state.subscribed);
    }

    return () => {
      isMountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const handleSubscriptionResult = async (subscribed: boolean) => {
    if (subscribed) {
      addToast(eventTitleForPassport || name, true);
      await updateSubscribe(true);
    } else {
      await updateSubscribe(false);
    }
    if (isMountedRef.current) {
      setLoadingState(LoadingState.None);
    }
  };

  const handleCalendarSubcription = async (isSubscribing: boolean): Promise<void> => {
    if (!data?.email) return;

    setLoadingState(isSubscribing ? LoadingState.Subscribing : LoadingState.Unsubscribing);

    try {
      const subscriptionResponse = await preRegisterToCalendarInvite({
        attendeeEmail: data.email,
        eventId: eventId.toString(),
        meetingId: meetingId,
      });
      await updateData(subscriptionResponse, isSubscribing);
    } catch (error) {
      setLoadingState(LoadingState.None);
      addToast(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleSubscription = async (): Promise<void> => {
    await handleCalendarSubcription(true);
  };

  const updateData = async (data: RegistrationSubscriptionParams, subscribeCalendar: boolean): Promise<void> => {
    try {
      if (data){
        await subscribe({ registrationId: data.registrationId, appSyncToken: data.appSyncToken });
        subscribeCalendar ? await subscribeToCalendarInvite(data.registrationId) : await unSubscribeToCalendar(data.registrationId);
      }
    } catch (error) {
      setLoadingState(LoadingState.None);
      addToast(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleUnsubscribe = async (): Promise<void> => {
    await handleCalendarSubcription(false);
  };

  return (
    <div className={`${iconButton ? 'calendar-container' : ''} flex justify-between`}>
      {calendarInvite !== CALENDAR_INVITES.Download &&
        (attendeeSubscribed && loadingState !== LoadingState.Unsubscribing
          ? (
            <Buttons.OptionsButton
              label={subscribedButtonLabel}
              name={subscribedButtonLabel}
              disabled={isButtonDisabled}
              eventId={eventId}
              eventName={name}
              className={`${className} outline-none on-hover-shadow flex items-center pt-px ${isEventLevelSubscription ? 'mt-4' : ''}`}
              unSubscribeToCalendar={handleUnsubscribe}
              downloadIcsFile={downloadIcsFile}
              openGoogleCalendar={openGoogleCalendar}
              isSubscribeToEvent={isSubscribeToEvent}
              calendarInvite={calendarInvite}
              isSubscribed
              withOptionsBoxStyle={withOptionsBoxStyle}
              iconButton={iconButton}
              onShowDropDown={onShowDropDown}
              customStyles={customStyles}
            />
          )
          : loadingState === LoadingState.Subscribing
            ? (
              <Buttons.Common
                name="Subscribing to Event"
                className={`subscribe-processing-btn ${customClassName}`}
              >
                <span className="preview-spinner animate-spin" />
                <label className="font-size-11px">{isEventLevelSubscription ? 'Subscribing to Event...' : 'Subscribing...'}</label>
              </Buttons.Common>
            )
            : loadingState === LoadingState.Unsubscribing
              ? (
                <Buttons.Common
                  name="UnSubscribing to Event"
                  className={`subscribe-processing-btn ${customClassName}`}
                >
                  <span className="preview-spinner animate-spin" />
                  <label className="font-size-11px">{isEventLevelSubscription ? 'Unsubscribing to Event...' : 'Unsubscribing...'}</label>
                </Buttons.Common>
              )
              : (
                <Buttons.Common
                  name={subscribeButtonLabel}
                  className={`${iconButton ? 'p-2 pr-0' : 'editable-welcome--subscribe-title'} ${customClassName}`}
                  onClick={handleSubscription}
                  disabled={isButtonDisabled}
                >
                  <Icon.AddToCalendar
                    width={iconSize}
                    height={iconSize}
                    viewBox="0 0 50 50"
                    className="fill-primary col order-first mr-2"
                  />
                  {!iconButton && <label className="text-primary">{subscribeButtonLabel}</label>}
                </Buttons.Common>
              )
        )
      }
      {calendarInvite === CALENDAR_INVITES.Download &&
        <div className={`${isEventLevelSubscription ? ' my-6 ml-5' : ''}`}>
          <Buttons.OptionsButton
            label={isEventLevelSubscription ? 'Add to Calendar' : '+ Add to Calendar'}
            name="addToCalendar"
            disabled={isButtonDisabled}
            eventId={eventId}
            eventName={name}
            className={`rounded on-hover-shadow flex ${isEventLevelSubscription ? 'connect-to-meeting-btn bg-primary text-white' : ''}`}
            downloadIcsFile={downloadIcsFile}
            openGoogleCalendar={openGoogleCalendar}
            withCalendarIcon={withCalendarIcon}
            withOptionsBoxStyle={withOptionsBoxStyle}
            iconButton={iconButton}
            onShowDropDown={onShowDropDown}
            customStyles={customStyles}
          />
        </div>
      }
    </div>
  );
};

export default SubscriptionCalendarButtons;
